/**
 * Reactivate a tutor: eligibility gate, then status/situation preview.
 */
import * as React from 'react';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { InfoModal } from '@/components/common/modals/InfoModal';
import {
  useGetTutorRemoteReactivationEligibility,
  useReactivateTutorRemote,
} from '@/hooks/useUsers';
import { getErrorMessage } from '@/utils/errors-utils';
import { formatReactivationPreview } from './tutor-form-utils';

/** Props for {@link ReactivateTutorModal}. */
interface ReactivateTutorModalProps {
  open: boolean;
  userId: string;
  companyId: string;
  onClose: () => void;
}

/** Reactivate a tutor: eligibility gate, then status/situation preview. */
export const ReactivateTutorModal: React.FC<Readonly<ReactivateTutorModalProps>> = ({
  open,
  userId,
  companyId,
  onClose,
}) => {
  const eligibility = useGetTutorRemoteReactivationEligibility(userId, companyId, {
    enabled: open && !!userId,
  });
  const reactivate = useReactivateTutorRemote();

  async function handleConfirm(): Promise<void> {
    await reactivate.mutateAsync({ userId, companyId });
  }

  /** Status / situation before → after, plus the not-auto-active warning. */
  function renderPreview(): React.ReactNode {
    const preview = eligibility.data?.preview;
    if (!preview) return null;
    const labels = formatReactivationPreview(preview);
    return (
      <div className="space-y-2 text-sm" style={{ color: 'var(--color-1-nuance-1)' }}>
        <p>
          Statut : {labels.currentStatus} → {labels.nextStatus}
        </p>
        <p>
          Situation : {labels.currentSituation} → {labels.nextSituation}
        </p>
        <p>{getErrorMessage('reactivationNotAutoActive')}</p>
      </div>
    );
  }

  if (!open) return null;

  if (eligibility.isLoading) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation"
        size="md"
        sections={[{ id: 'loading', content: 'Vérification des conditions de réactivation…' }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  if (eligibility.isError) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation impossible"
        size="md"
        sections={[{ id: 'error', content: getErrorMessage('reactivationEligibilityFailed') }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  if (!eligibility.data?.canReactivate) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation impossible"
        size="md"
        sections={[{ id: 'blocked', content: getErrorMessage('reactivationNotEligible') }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  return (
    <ConfirmModal
      open
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Réactiver ce tuteur"
      description={getErrorMessage('reactivationConfirmRecalc')}
      confirmText="Réactiver"
      variant="primary"
      isLoading={reactivate.isPending}
    >
      {renderPreview()}
    </ConfirmModal>
  );
};
